import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ArrowUpRight, Download } from 'lucide-react';
import {
  getDeepLinkForPath,
  getStoreUrlForDevice,
  isAndroidDevice,
  isIOSDevice,
  isMobileDevice,
  isStandaloneMode,
  openInAppOrStore,
} from '../../utils/device';

const SESSION_DISMISS_KEY = 'candle_mobile_app_banner_dismissed';

function isSuppressedPath(pathname: string): boolean {
  if (/^\/m\/[^/]+/i.test(pathname)) return true;
  if (/^\/idv(\/|$)/i.test(pathname)) return true;
  if (/^\/memorial\/[^/]+\/obituary\/(embed|print)\/?$/i.test(pathname)) return true;
  return false;
}

export function MobileAppBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed || isStandaloneMode() || isSuppressedPath(location.pathname)) {
    return null;
  }

  const deepLink = getDeepLinkForPath(location.pathname, location.search);
  const canOpenDeepLink = Boolean(deepLink) && isMobileDevice();
  const storeUrl = getStoreUrlForDevice();

  const storeHint = isAndroidDevice()
    ? t('plaqueSetup.googlePlay.label', 'Google Play')
    : isIOSDevice()
      ? t('plaqueSetup.appStore.label', 'App Store')
      : t('mobileAppBanner.iosAndAndroid', 'iOS & Android');

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      // Ignore storage errors in private mode
    }
  };

  const handlePrimaryClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (canOpenDeepLink && deepLink) {
      e.preventDefault();
      openInAppOrStore(deepLink);
    }
  };

  return (
    <div
      role="region"
      aria-label={t('mobileAppBanner.ariaLabel', 'Candle mobile app')}
      className="md:hidden flex-shrink-0 w-full border-b border-amber-500/20 bg-gradient-to-r from-neutral-950 via-neutral-900 to-neutral-950 text-white px-3 py-2 z-[60]"
    >
      <div className="mx-auto flex items-center justify-between gap-2.5 max-w-xl">
        <button
          type="button"
          onClick={handleDismiss}
          className="-ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-white transition-colors"
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <img
            src="/apple-touch-icon.png"
            alt="Candle Legacy"
            className="h-9 w-9 shrink-0 rounded-xl border border-amber-500/30 shadow-sm object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="truncate text-xs font-semibold text-white leading-tight">
                {t('mobileAppBanner.title', 'Candle Legacy App')}
              </p>
              <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                {storeHint}
              </span>
            </div>
            <p className="truncate text-[11px] text-neutral-300 leading-tight mt-0.5">
              {canOpenDeepLink
                ? t('mobileAppBanner.openSubtitle', 'Open this page in the Candle app')
                : t('mobileAppBanner.installSubtitle', 'Ceremony reminders & memorials on the go')}
            </p>
          </div>
        </div>

        <a
          href={canOpenDeepLink && deepLink ? deepLink : storeUrl}
          target={canOpenDeepLink ? undefined : '_blank'}
          rel={canOpenDeepLink ? undefined : 'noopener noreferrer'}
          onClick={handlePrimaryClick}
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-3.5 text-xs font-semibold text-neutral-950 shadow-sm shadow-amber-500/20 active:scale-95 transition-all"
        >
          {canOpenDeepLink ? (
            <>
              {t('mobileAppBanner.open', 'Open')}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5" />
              {t('mobileAppBanner.getApp', 'Get App')}
            </>
          )}
        </a>
      </div>
    </div>
  );
}
